import React from "react";
import {
  Box,
  Paper,
  Typography,
  Stack,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  alpha,
  useTheme,
  Skeleton,
  Button,
} from "@mui/material";
import {
  CheckCircle as CheckCircleIcon,
  WarningAmber as WarningIcon,
  Cancel as CancelIcon,
  HourglassEmpty as HourglassEmptyIcon,
  ReceiptLong as ReceiptLongIcon,
} from "@mui/icons-material";
import {
  formatBillingCurrency,
  formatDashedDateWithYear,
} from "../../../utils/hooks/dataHandler";

const STATUS_CONFIG = {
  paid: {
    label: "Paid",
    color: "success",
    icon: <CheckCircleIcon fontSize='small' />,
    bgColor: (theme) => alpha(theme.palette.success.main, 0.08),
  },
  unpaid: {
    label: "Unpaid",
    color: "warning",
    icon: <WarningIcon fontSize='small' />,
    bgColor: (theme) => alpha(theme.palette.warning.main, 0.08),
  },
  failed: {
    label: "Failed",
    color: "error",
    icon: <CancelIcon fontSize='small' />,
    bgColor: (theme) => alpha(theme.palette.error.main, 0.08),
  },
  trial: {
    label: "Trial",
    color: "primary",
    icon: <CheckCircleIcon fontSize='small' />,
    bgColor: (theme) => alpha(theme.palette.info.main, 0.08),
  },
  pending: {
    label: "Pending",
    color: "info",
    icon: <HourglassEmptyIcon fontSize='small' />,
    bgColor: (theme) => alpha(theme.palette.info.main, 0.08),
  },
  default: {
    label: "Unknown",
    color: "default",
    icon: <HourglassEmptyIcon fontSize='small' />,
    bgColor: (theme) => theme.palette.grey[100],
  },
};

const CHANGE_TYPE_TEXT = {
  add_user: "Sub-User Added",
  remove_user: "Sub-User Removed",
  add_company: "Company Added",
  remove_company: "Company Removed",
  renew_subscription: "Subscription Renewed",
  cancel_subscription: "Subscription Cancelled",
  update_plan: "Plan Updated",
  // Add more as needed
};

// --- 3. 金额渲染组件 ---
const AmountCell = ({ amount, discountAmount, currency, discount, status }) => {
  const theme = useTheme();

  // Case 1: No Charge (Trial or $0)
  if (status?.toLowerCase() === "trial" || amount <= 0) {
    return (
      <Typography
        variant='body2'
        color='text.secondary'
        sx={{
          fontStyle: "italic",
          fontWeight: 500,
        }}
      >
        Included
      </Typography>
    );
  }

  // Case 2: Has Discount
  if (discount && discount > 0) {
    const finalAmount = discountAmount || amount - (amount * discount) / 100;
    return (
      <Stack spacing={0.5} alignItems='flex-end'>
        <Typography
          variant='caption'
          sx={{
            textDecoration: "line-through",
            color: "text.disabled",
          }}
        >
          {formatBillingCurrency(amount, currency)}
        </Typography>
        <Typography variant='subtitle2' fontWeight={700} color='primary.main'>
          {formatBillingCurrency(finalAmount, currency)}
        </Typography>
        <Chip
          label={`${discount}% OFF`}
          size='small'
          sx={{
            height: 18,
            fontSize: "0.6rem",
            bgcolor: alpha(theme.palette.primary.main, 0.1),
            color: theme.palette.primary.dark,
            fontWeight: 600,
          }}
        />
      </Stack>
    );
  }

  // Case 3: Regular Amount
  return (
    <Typography variant='subtitle2' fontWeight={600}>
      {formatBillingCurrency(amount, currency)}
    </Typography>
  );
};

// --- 4. 状态徽章组件 ---
const StatusBadge = ({ status }) => {
  const config = STATUS_CONFIG[status?.toLowerCase()] || STATUS_CONFIG.default;

  return (
    <Chip
      label={config.label}
      size='small'
      sx={{
        fontWeight: 600,
        textTransform: "capitalize",
        bgcolor: (theme) =>
          theme.palette[config.color]?.main || theme.palette.background.default,
        color: (theme) =>
          theme.palette[config.color]?.contrastText ||
          theme.palette.text.primary,
        borderColor: (theme) =>
          config.color !== "default"
            ? alpha(theme.palette[config.color]?.main, 0.3)
            : "divider",
        "& .MuiChip-icon": {
          mr: 0.5,
        },
      }}
    />
  );
};

// --- 5. 空状态组件 ---
const EmptyState = () => (
  <Paper
    variant='outlined'
    sx={{
      p: 4,
      borderRadius: 3,
      borderStyle: "dashed",
      borderColor: "divider",
      textAlign: "center",
      backgroundColor: "background.paper",
    }}
  >
    <Box
      sx={{
        width: 64,
        height: 64,
        borderRadius: "50%",
        backgroundColor: "grey.100",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        mx: "auto",
        mb: 2,
      }}
    >
      <ReceiptLongIcon sx={{ fontSize: 32, color: "text.secondary" }} />
    </Box>
    <Typography variant='h6' fontWeight={600} color='text.primary' mb={1}>
      No Unpaid Usage
    </Typography>
    <Typography variant='body2' color='text.secondary' mb={3}>
      Your subscription usages will appear here once you start using paid
      features.
    </Typography>
  </Paper>
);

// --- 6. 骨架屏 (加载状态) ---
const LoadingSkeleton = () => (
  <Paper variant='outlined' sx={{ borderRadius: 3, overflow: "hidden" }}>
    <TableContainer>
      <Table>
        <TableBody>
          {[...Array(5)].map((_, index) => (
            <TableRow key={index}>
              <TableCell>
                <Skeleton width='60%' />
                <Skeleton
                  width='80%'
                  variant='text'
                  sx={{ fontSize: "0.75rem" }}
                />
              </TableCell>
              <TableCell align='right'>
                <Skeleton width={80} sx={{ ml: "auto" }} />
              </TableCell>
              <TableCell align='center'>
                <Skeleton
                  width={60}
                  height={24}
                  sx={{ borderRadius: 1, mx: "auto" }}
                />
              </TableCell>
              <TableCell align='center'>
                <Skeleton width={90} sx={{ mx: "auto" }} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  </Paper>
);

// --- 7. 主组件 ---
const BillingHistorySection = ({
  billingHistory = [],
  discount = 0,
  next_billing_date,
  isLoading = false,
}) => {
  const theme = useTheme();
  const hasData = Array.isArray(billingHistory) && billingHistory.length > 0;

  return (
    <Box>
      {/* Content */}
      {isLoading ? (
        <LoadingSkeleton />
      ) : !hasData ? (
        <EmptyState />
      ) : (
        <Paper
          variant='outlined'
          sx={{
            borderRadius: 3,
            borderColor: "divider",
            overflow: "hidden",
            boxShadow: theme.shadows[1],
          }}
        >
          <TableContainer sx={{ maxHeight: 480 }}>
            <Table
              stickyHeader
              size='medium'
              aria-label='billing history table'
            >
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      color: "text.primary",
                      backgroundColor: "background.default",
                      borderBottom: 2,
                      borderColor: "divider",
                    }}
                  >
                    Description
                  </TableCell>
                  <TableCell
                    align='right'
                    sx={{
                      fontWeight: 700,
                      color: "text.primary",
                      backgroundColor: "background.default",
                      borderBottom: 2,
                      borderColor: "divider",
                    }}
                  >
                    Amount
                  </TableCell>
                  <TableCell
                    align='center'
                    sx={{
                      fontWeight: 700,
                      color: "text.primary",
                      backgroundColor: "background.default",
                      borderBottom: 2,
                      borderColor: "divider",
                    }}
                  >
                    Status
                  </TableCell>
                  <TableCell
                    align='center'
                    sx={{
                      fontWeight: 700,
                      color: "text.primary",
                      backgroundColor: "background.default",
                      borderBottom: 2,
                      borderColor: "divider",
                    }}
                  >
                    Date
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {billingHistory.map((entry, index) => {
                  const changeType =
                    CHANGE_TYPE_TEXT[entry.change_type] ||
                    entry.change_type ||
                    "Transaction";

                  return (
                    <TableRow
                      key={entry.billing_id || index}
                      hover
                      sx={{
                        "&:hover": {
                          backgroundColor: alpha(
                            theme.palette.primary.main,
                            0.02,
                          ),
                        },
                        transition:
                          theme.transitions.create("background-color"),
                      }}
                    >
                      {/* Description */}
                      <TableCell sx={{ py: 2.5, minWidth: 200 }}>
                        <Typography variant='subtitle2' fontWeight={600} noWrap>
                          {changeType}
                        </Typography>
                        {entry.description && (
                          <Typography
                            variant='caption'
                            color='text.secondary'
                            sx={{
                              display: "-webkit-box",
                              WebkitLineClamp: 1,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                            }}
                          >
                            {entry.description}
                          </Typography>
                        )}
                      </TableCell>

                      {/* Amount */}
                      <TableCell align='right' sx={{ py: 2.5 }}>
                        <AmountCell
                          amount={entry.extra_fee_cents}
                          discountAmount={entry.charge_after_discount}
                          currency={entry.currency}
                          discount={entry.discount}
                          status={entry.status}
                        />
                      </TableCell>

                      {/* Status */}
                      <TableCell align='center' sx={{ py: 2.5 }}>
                        <StatusBadge status={entry.status} />
                      </TableCell>

                      {/* Date */}
                      <TableCell
                        align='center'
                        sx={{ py: 2.5, color: "text.secondary" }}
                      >
                        {formatDashedDateWithYear(entry.created_at)}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Footer Summary */}
          <Box
            sx={{
              px: 3,
              py: 2,
              backgroundColor: "grey.50",
              borderTop: 1,
              borderColor: "divider",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 1,
            }}
          >
            <Typography variant='body2' color='text.secondary'>
              Showing {billingHistory.length}{" "}
              {billingHistory.length === 1 ? "transaction" : "transactions"}
            </Typography>
            {next_billing_date && (
              <Typography variant='subtitle2' color='primary' fontWeight={600}>
                Next Billing: {formatDashedDateWithYear(next_billing_date)}
              </Typography>
            )}
          </Box>
        </Paper>
      )}
    </Box>
  );
};

export default BillingHistorySection;
